import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAppContext } from '@/context/AppContext';
import { useToken } from '@/lib/useToken';
import { apiFetch } from '@/lib/api';
import { RULES } from '@/data/rules';
import type { Rule } from '@/types/entities';
import type { RuleCondition, ConditionType, ConditionOperator } from '@/types/condition';

// ── API response types ──────────────────────────────────────────────────────

interface ApiMatchCondition {
  type: 'pattern' | 'regex' | 'cidr';
  field: 'app' | 'title' | 'url' | 'site' | 'ip' | 'document';
  value: string;
}

interface ApiRule {
  id: string;
  name: string;
  priority: number;
  match: { conditions: ApiMatchCondition[]; mode?: 'all' | 'any' };
  projectId: string;
  enabled: boolean;
  orgId: string;
  businessUnitId: string | null;
  createdAt: string;
  project: {
    id: string;
    name: string;
    billable: boolean;
    client: { id: string; name: string } | null;
  };
}

// ── Mappers ─────────────────────────────────────────────────────────────────

const FIELD_TO_TYPE: Record<ApiMatchCondition['field'], ConditionType> = {
  app: 'APP',
  title: 'TITLE',
  url: 'URL',
  site: 'SITE',
  ip: 'IP',
  document: 'DOCUMENT',
};

const MATCH_TYPE_TO_OPERATOR: Record<ApiMatchCondition['type'], ConditionOperator> = {
  pattern: 'contains',
  regex: 'matches',
  cidr: 'is',
};

function mapCondition(c: ApiMatchCondition): RuleCondition {
  return {
    type: FIELD_TO_TYPE[c.field],
    operator: MATCH_TYPE_TO_OPERATOR[c.type],
    value: c.value,
  };
}

function mapRule(r: ApiRule): Rule {
  return {
    id: r.id,
    priority: r.priority,
    name: r.name,
    projectId: r.projectId,
    status: r.enabled ? 'enabled' : 'disabled',
    // Default to 'all' so historical rows that pre-date task 191 (where
    // `mode` was added) keep matching their AND semantics. The engine
    // applies the same default at evaluation time.
    mode: r.match.mode ?? 'all',
    conditions: r.match.conditions.map(mapCondition),
  };
}

// ── List hook ───────────────────────────────────────────────────────────────

interface UseRulesResult {
  data: readonly Rule[];
  isLoading: boolean;
}

export function useRules(): UseRulesResult {
  const { mockupMode, currentOrg } = useAppContext();
  const getToken = useToken();

  const query = useQuery({
    queryKey: ['rules', currentOrg.id],
    queryFn: async () => {
      const token = await getToken();
      return apiFetch<{ rules: ApiRule[] }>(`/api/v1/orgs/${currentOrg.id}/rules`, token);
    },
    enabled: !mockupMode && !!currentOrg.id,
  });

  if (mockupMode) return { data: RULES, isLoading: false };

  if (query.isLoading || !query.data) {
    return { data: [], isLoading: query.isLoading };
  }

  return { data: query.data.rules.map(mapRule), isLoading: false };
}

// ── Mutation hooks ──────────────────────────────────────────────────────────

export function useCreateRule() {
  const { currentOrg } = useAppContext();
  const getToken = useToken();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (body: {
      // Task 191 D-A: name is optional. The backend synthesizes a default
      // from the first match condition when omitted.
      name?: string;
      priority: number;
      match: { conditions: ApiMatchCondition[]; mode?: 'all' | 'any' };
      projectId: string;
      enabled?: boolean;
    }) => {
      const token = await getToken();
      return apiFetch<ApiRule>(`/api/v1/orgs/${currentOrg.id}/rules`, token, {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['rules', currentOrg.id] });
    },
  });
}

export function useUpdateRule() {
  const { currentOrg } = useAppContext();
  const getToken = useToken();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      ...body
    }: {
      id: string;
      name?: string;
      priority?: number;
      match?: { conditions: ApiMatchCondition[]; mode?: 'all' | 'any' };
      projectId?: string;
      enabled?: boolean;
    }) => {
      const token = await getToken();
      return apiFetch<ApiRule>(`/api/v1/orgs/${currentOrg.id}/rules/${id}`, token, {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['rules', currentOrg.id] });
    },
  });
}

export function useDeleteRule() {
  const { currentOrg } = useAppContext();
  const getToken = useToken();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const token = await getToken();
      return apiFetch<undefined>(`/api/v1/orgs/${currentOrg.id}/rules/${id}`, token, {
        method: 'DELETE',
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['rules', currentOrg.id] });
    },
  });
}

export function useReorderRules() {
  const { currentOrg } = useAppContext();
  const getToken = useToken();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (ruleIds: string[]) => {
      const token = await getToken();
      return apiFetch<{ rules: ApiRule[] }>(`/api/v1/orgs/${currentOrg.id}/rules/reorder`, token, {
        method: 'PUT',
        body: JSON.stringify({ ruleIds }),
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['rules', currentOrg.id] });
    },
  });
}

// ── Reclassify (task 132 D4) ────────────────────────────────────────────────
//
// Admin-triggered org-wide reclassify. Fire-and-forget: the mutation
// resolves when the backend has enqueued the job; the dashboard and
// timesheet queries re-run on success but will not reflect the new
// classifications until the job itself completes (about a minute for a
// typical tenant). Matches the sync-now UX from task 109.
//
// `onSuccess` invalidates every surface that reads classified_event —
// dashboard, categorize groups, and employees-with-stats — plus `rules`
// itself so any per-rule event-count column re-fetches. All keyed on
// currentOrg.id so stale cache entries for other orgs are untouched.

export function useReclassify() {
  const { mockupMode, currentOrg } = useAppContext();
  const getToken = useToken();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (): Promise<{ jobId: string; status: 'queued' }> => {
      if (mockupMode) {
        // Mockup-mode short-circuit: keep the full confirm → pending →
        // success UX working without a network call. The 600 ms delay
        // lets the "Reclassifying…" pending state render at least one
        // frame so operators see the button swap labels.
        await new Promise((r) => setTimeout(r, 600));
        return { jobId: 'mock-reclassify-job', status: 'queued' };
      }
      const token = await getToken();
      return apiFetch<{ jobId: string; status: 'queued' }>(
        `/api/v1/orgs/${currentOrg.id}/reclassify`,
        token,
        { method: 'POST' },
      );
    },
    onSuccess: () => {
      // classified_event is the source of truth for the dashboard,
      // employee detail, timesheets, and the categorize groups — all
      // of those queries need to re-run on success, though none will
      // reflect the new state until the job actually completes. Also
      // invalidate rules itself so any per-rule event-count column
      // re-fetches.
      void qc.invalidateQueries({ queryKey: ['rules', currentOrg.id] });
      void qc.invalidateQueries({ queryKey: ['dashboard', currentOrg.id] });
      void qc.invalidateQueries({ queryKey: ['categorize-groups', currentOrg.id] });
      void qc.invalidateQueries({ queryKey: ['employees-with-stats', currentOrg.id] });
    },
  });
}
